import {isFeatureEnabled} from 'feature-flags';

import {VehicleTypeEnumObject} from '@dms/api/shared';
import {FuelTypeEnum, HybridTypeEnum, VehicleTypeEnum} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

/** Vehicle context passed to availability predicates. */
export interface FieldAvailabilityContext {
  vehicleType?: VehicleTypeEnum;
  fuelType?: FuelTypeEnum;
  hybridType?: HybridTypeEnum;
}

export interface MandatoryFieldMapValue {
  title: string;
  href: string;
  tab?: string;
  formField?: string;
  /**
   * When defined, the field is only treated as mandatory when this predicate returns `true`
   * for the current vehicle context.
   *
   * @example
   * isAvailableFor: notTrailerOrSemitrailer
   *
   * @example
   * isAvailableFor: (ctx) => notTrailerOrSemitrailer(ctx) && onlyForPlugInHybrid(ctx)
   */
  isAvailableFor?: (context: FieldAvailabilityContext) => boolean;
}

export interface MandatoryFieldMap {
  [key: string]: MandatoryFieldMapValue;
}

const excludeVehicleTypes =
  (...types: VehicleTypeEnumObject[]) =>
  (ctx: FieldAvailabilityContext): boolean =>
    // When vehicleType is undefined we treat the field as available (no restriction)
    ctx.vehicleType === undefined || !(types as string[]).includes(ctx.vehicleType);

const notTrailerOrSemitrailer = excludeVehicleTypes(
  VehicleTypeEnumObject.VEHICLETYPE_TRAILER,
  VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRAILER
);

const onlyPassengerCar = (ctx: FieldAvailabilityContext): boolean =>
  ctx.vehicleType === undefined ||
  ctx.vehicleType === VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR;

const PHEV_FUEL_TYPE: FuelTypeEnum = 'FUELTYPE_HYBRID';
const PHEV_HYBRID_TYPE: HybridTypeEnum = 'HYBRIDTYPE_PHEV';

const onlyForPlugInHybrid = (ctx: FieldAvailabilityContext): boolean =>
  ctx.fuelType === PHEV_FUEL_TYPE && ctx.hybridType === PHEV_HYBRID_TYPE;

const isPhevAndNotTrailer = (ctx: FieldAvailabilityContext): boolean =>
  notTrailerOrSemitrailer(ctx) && onlyForPlugInHybrid(ctx);

export const mandatoryFieldsMap: MandatoryFieldMap = {
  LICENSE_PLATE: {
    title: i18n.t('entity.vehicle.labels.licensePlate'),
    href: vehiclesRoutes.editV2,
    formField: 'registrationPlate',
  },
  VIN: {
    title: i18n.t('entity.vehicle.labels.vinCode'),
    href: vehiclesRoutes.editV2,
    formField: 'vin',
  },
  HSN: {
    title: i18n.t('entity.vehicle.labels.hsn'),
    href: vehiclesRoutes.editV2,
    formField: 'hsn',
  },
  TSN: {
    title: i18n.t('entity.vehicle.labels.tsn'),
    href: vehiclesRoutes.editV2,
    formField: 'tsn',
  },
  MODEL_RANGE: {
    title: i18n.t('entity.vehicle.labels.baureihe'),
    href: vehiclesRoutes.editV2,
    formField: 'modelRange',
    isAvailableFor: (ctx) =>
      onlyPassengerCar(ctx) && isFeatureEnabled(featureFlags.ADVERTISEMENT_PER_PLATFORM_ATTRIBUTES),
  },
  TRIM_LEVEL: {
    title: i18n.t('entity.vehicle.labels.trimLevel'),
    href: vehiclesRoutes.editV2,
    formField: 'trim',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  MODEL_DESCRIPTION: {
    title: i18n.t('entity.vehicle.labels.variantName'),
    href: vehiclesRoutes.editV2,
    formField: 'variant',
  },
  VEHICLE_TITLE: {
    title: i18n.t('entity.vehicle.labels.title'),
    href: vehiclesRoutes.editV2,
    formField: 'title',
  },
  FIRST_REGISTRATION: {
    title: i18n.t('entity.vehicle.labels.firstRegistration'),
    href: vehiclesRoutes.editV2,
    formField: 'firstRegistrationOn',
  },
  REAL_PRODUCTION_YEAR: {
    title: i18n.t('entity.vehicle.labels.realProductionYear'),
    href: vehiclesRoutes.editV2,
    formField: 'manufacturedOnYear',
  },
  REAL_PRODUCTION_MONTH: {
    title: i18n.t('entity.vehicle.labels.realProductionMonth'),
    href: vehiclesRoutes.editV2,
    formField: 'manufacturedOnMonth',
  },
  MODEL_YEAR: {
    title: i18n.t('entity.vehicle.labels.modelYear'),
    href: vehiclesRoutes.editV2,
    formField: 'modelYear',
  },
  BODY: {
    title: i18n.t('entity.vehicle.labels.body'),
    href: vehiclesRoutes.editV2,
    formField: 'bodyStyle',
  },
  FUEL: {
    title: i18n.t('entity.vehicle.labels.fuel'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelType',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  TRANSMISSION: {
    title: i18n.t('entity.vehicle.labels.transmission'),
    href: vehiclesRoutes.editV2,
    formField: 'transmission',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  DRIVE: {
    title: i18n.t('entity.vehicle.labels.drive'),
    href: vehiclesRoutes.editV2,
    formField: 'drive',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  POWER: {
    title: i18n.t('entity.vehicle.labels.power2'),
    href: vehiclesRoutes.editV2,
    formField: 'power',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  MAXIMUM_POWER: {
    title: i18n.t('entity.vehicle.labels.maximalPower'),
    href: vehiclesRoutes.editV2,
    formField: 'maximalPower',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  ENGINE_CAPACITY: {
    title: i18n.t('entity.vehicle.labels.engineCapacity'),
    href: vehiclesRoutes.editV2,
    formField: 'engineVolume',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  NUMBER_OF_CYLINDERS: {
    title: i18n.t('entity.vehicle.labels.numberOfCylinders2'),
    href: vehiclesRoutes.editV2,
    formField: 'cylinderCount',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  NUMBER_OF_GEARS: {
    title: i18n.t('entity.vehicle.labels.numberOfGears2'),
    href: vehiclesRoutes.editV2,
    formField: 'gearCount',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  NUMBER_OF_DOORS: {
    title: i18n.t('entity.vehicle.labels.numberOfDoorsFull'),
    href: vehiclesRoutes.editV2,
    formField: 'doorCountCategory',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  NUMBER_OF_SEATS: {
    title: i18n.t('entity.vehicle.labels.numberOfSeatsFull'),
    href: vehiclesRoutes.editV2,
    formField: 'seatCount',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  BODY_COLOR_TYPE: {
    title: i18n.t('entity.vehicle.labels.bodyColorType'),
    href: vehiclesRoutes.editV2,
    formField: 'exteriorColorSpecification',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  BODY_COLOR: {
    title: i18n.t('entity.vehicle.labels.bodyColor'),
    href: vehiclesRoutes.editV2,
    formField: 'exteriorColor',
  },
  FACTORY_COLOR_NAME: {
    title: i18n.t('entity.vehicle.labels.colorName'),
    href: vehiclesRoutes.editV2,
    formField: 'manufacturerColorName',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  FACTORY_COLOR_CODE: {
    title: i18n.t('entity.vehicle.labels.manufacturerColorCode'),
    href: vehiclesRoutes.editV2,
    formField: 'manufacturerColorCode',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  INTERIOR_MATERIAL: {
    title: i18n.t('entity.vehicle.labels.interiorMaterial'),
    href: vehiclesRoutes.editV2,
    formField: 'interiorMaterial',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  INTERIOR_COLOR: {
    title: i18n.t('entity.vehicle.labels.interiorColor'),
    href: vehiclesRoutes.editV2,
    formField: 'interiorColor',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  INTERIOR_COLOR_NAME: {
    title: i18n.t('entity.vehicle.labels.interiorColorName'),
    href: vehiclesRoutes.editV2,
    formField: 'interiorColorName',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  ENGINE_NAME: {
    title: i18n.t('entity.vehicle.labels.engineName'),
    href: vehiclesRoutes.editV2,
    formField: 'engineName',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  ENGINE_NUMBER: {
    title: i18n.t('entity.vehicle.labels.engineNumber'),
    href: vehiclesRoutes.editV2,
    formField: 'engineNumber',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  ENGINE_CODE: {
    title: i18n.t('entity.vehicle.labels.engineCode'),
    href: vehiclesRoutes.editV2,
    formField: 'engineCode',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  TRANSMISSION_CODE: {
    title: i18n.t('entity.vehicle.labels.transmissionCode'),
    href: vehiclesRoutes.editV2,
    formField: 'transmissionCode',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  TRANSMISSION_NUMBER: {
    title: i18n.t('entity.vehicle.labels.transmissionNumber'),
    href: vehiclesRoutes.editV2,
    formField: 'transmissionNumber',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  CONSUMPTION_COMBINED: {
    title: i18n.t('entity.vehicle.labels.combined'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionCombined',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  CONSUMPTION_URBAN: {
    title: i18n.t('entity.vehicle.labels.urban'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionUrban',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  CONSUMPTION_EXTRA_URBAN: {
    title: i18n.t('entity.vehicle.labels.extraUrban'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionExtraUrban',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  // WLTP consumption fields (l or kg)
  COMBINED_CONSUMPTION: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCombined'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpCombined',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  CITY_CENTER_CONSUMPTION: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCityCenter'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpCityCenter',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  OUTSKIRTS_CONSUMPTION: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpOutskirts'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpOutskirts',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  COUNTRY_ROADS_CONSUMPTION: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCountryRoads'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpCountryRoads',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  HIGHWAY_CONSUMPTION: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpHighway'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpHighway',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  WEIGHTED_CONSUMPTION: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpWeighted'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpWeighted',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  // WLTP consumption fields (kWh) — only applicable for plug-in hybrids (PHEV)
  COMBINED_CONSUMPTION_KWH: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCombined'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpCombinedKwh',
    isAvailableFor: isPhevAndNotTrailer,
  },
  CITY_CENTER_CONSUMPTION_KWH: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCityCenter'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpCityCenterKwh',
    isAvailableFor: isPhevAndNotTrailer,
  },
  OUTSKIRTS_CONSUMPTION_KWH: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpOutskirts'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpOutskirtsKwh',
    isAvailableFor: isPhevAndNotTrailer,
  },
  COUNTRY_ROADS_CONSUMPTION_KWH: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpCountryRoads'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpCountryRoadsKwh',
    isAvailableFor: isPhevAndNotTrailer,
  },
  HIGHWAY_CONSUMPTION_KWH: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpHighway'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpHighwayKwh',
    isAvailableFor: isPhevAndNotTrailer,
  },
  WEIGHTED_CONSUMPTION_KWH: {
    title: i18n.t('entity.vehicle.labels.fuelConsumptionWltpWeightedCombinedKwh'),
    href: vehiclesRoutes.editV2,
    formField: 'fuelConsumptionWltpWeightedCombinedKwh',
    isAvailableFor: isPhevAndNotTrailer,
  },
  EMISSION_CLASS: {
    title: i18n.t('entity.vehicle.labels.emissionClass'),
    href: vehiclesRoutes.editV2,
    formField: 'emissionClass',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  EMISSIONS_CO2: {
    title: i18n.t('entity.vehicle.labels.emissionsCo2WO'),
    href: vehiclesRoutes.editV2,
    formField: 'carbonDioxideEmission',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  VEHICLE_LENGTH: {
    title: i18n.t('entity.vehicle.labels.vehicleLength'),
    href: vehiclesRoutes.editV2,
    formField: 'lengthCategory',
    isAvailableFor: excludeVehicleTypes(
      VehicleTypeEnumObject.VEHICLETYPE_CARAVAN,
      VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
      VehicleTypeEnumObject.VEHICLETYPE_MOTORCYCLE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRAILER,
      VehicleTypeEnumObject.VEHICLETYPE_TRAILER,
      VehicleTypeEnumObject.VEHICLETYPE_BUS,
      VehicleTypeEnumObject.VEHICLETYPE_OTHER,
      VehicleTypeEnumObject.VEHICLETYPE_AGRICULTURAL_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_CONSTRUCTION_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRUCK
    ),
  },
  VEHICLE_HEIGHT: {
    title: i18n.t('entity.vehicle.labels.vehicleHeight'),
    href: vehiclesRoutes.editV2,
    formField: 'heightCategory',
    isAvailableFor: excludeVehicleTypes(
      VehicleTypeEnumObject.VEHICLETYPE_CARAVAN,
      VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
      VehicleTypeEnumObject.VEHICLETYPE_MOTORCYCLE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRAILER,
      VehicleTypeEnumObject.VEHICLETYPE_TRAILER,
      VehicleTypeEnumObject.VEHICLETYPE_BUS,
      VehicleTypeEnumObject.VEHICLETYPE_OTHER,
      VehicleTypeEnumObject.VEHICLETYPE_AGRICULTURAL_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_CONSTRUCTION_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRUCK
    ),
  },
  LENGTH: {
    title: i18n.t('general.labels.length'),
    href: vehiclesRoutes.editV2,
    formField: 'dimensions.length',
  },
  WIDTH: {
    title: i18n.t('general.labels.width'),
    href: vehiclesRoutes.editV2,
    formField: 'dimensions.width',
  },
  HEIGHT: {
    title: i18n.t('general.labels.height'),
    href: vehiclesRoutes.editV2,
    formField: 'dimensions.height',
  },
  LOAD_SPACE_LENGTH: {
    title: i18n.t('entity.vehicle.labels.loadSpaceLength'),
    href: vehiclesRoutes.editV2,
    formField: 'dimensionsLoadingSpace.length',
    isAvailableFor: excludeVehicleTypes(
      VehicleTypeEnumObject.VEHICLETYPE_CARAVAN,
      VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
      VehicleTypeEnumObject.VEHICLETYPE_MOTORCYCLE,
      VehicleTypeEnumObject.VEHICLETYPE_BUS,
      VehicleTypeEnumObject.VEHICLETYPE_OTHER,
      VehicleTypeEnumObject.VEHICLETYPE_AGRICULTURAL_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_CONSTRUCTION_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRUCK
    ),
  },
  LOAD_SPACE_WIDTH: {
    title: i18n.t('entity.vehicle.labels.loadSpaceWidth'),
    href: vehiclesRoutes.editV2,
    formField: 'dimensionsLoadingSpace.width',
    isAvailableFor: excludeVehicleTypes(
      VehicleTypeEnumObject.VEHICLETYPE_CARAVAN,
      VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
      VehicleTypeEnumObject.VEHICLETYPE_MOTORCYCLE,
      VehicleTypeEnumObject.VEHICLETYPE_BUS,
      VehicleTypeEnumObject.VEHICLETYPE_OTHER,
      VehicleTypeEnumObject.VEHICLETYPE_AGRICULTURAL_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_CONSTRUCTION_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRUCK
    ),
  },
  LOAD_SPACE_HEIGHT: {
    title: i18n.t('entity.vehicle.labels.loadSpaceHeight'),
    href: vehiclesRoutes.editV2,
    formField: 'dimensionsLoadingSpace.height',
    isAvailableFor: excludeVehicleTypes(
      VehicleTypeEnumObject.VEHICLETYPE_CARAVAN,
      VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
      VehicleTypeEnumObject.VEHICLETYPE_MOTORCYCLE,
      VehicleTypeEnumObject.VEHICLETYPE_BUS,
      VehicleTypeEnumObject.VEHICLETYPE_OTHER,
      VehicleTypeEnumObject.VEHICLETYPE_AGRICULTURAL_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_CONSTRUCTION_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRUCK
    ),
  },
  LOAD_AREA: {
    title: i18n.t('entity.vehicle.labels.loadArea'),
    href: vehiclesRoutes.editV2,
    formField: 'loadingArea',
    isAvailableFor: excludeVehicleTypes(
      VehicleTypeEnumObject.VEHICLETYPE_CARAVAN,
      VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
      VehicleTypeEnumObject.VEHICLETYPE_MOTORCYCLE,
      VehicleTypeEnumObject.VEHICLETYPE_BUS,
      VehicleTypeEnumObject.VEHICLETYPE_OTHER,
      VehicleTypeEnumObject.VEHICLETYPE_AGRICULTURAL_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_CONSTRUCTION_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRUCK
    ),
  },
  EUROPALLET_STORAGE_SPACES: {
    title: i18n.t('entity.vehicle.labels.europalletStorageSpaces'),
    href: vehiclesRoutes.editV2,
    formField: 'europalletStorageSpaces',
    isAvailableFor: excludeVehicleTypes(
      VehicleTypeEnumObject.VEHICLETYPE_CARAVAN,
      VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
      VehicleTypeEnumObject.VEHICLETYPE_VAN,
      VehicleTypeEnumObject.VEHICLETYPE_MOTORCYCLE,
      VehicleTypeEnumObject.VEHICLETYPE_TRUCK,
      VehicleTypeEnumObject.VEHICLETYPE_BUS,
      VehicleTypeEnumObject.VEHICLETYPE_OTHER,
      VehicleTypeEnumObject.VEHICLETYPE_AGRICULTURAL_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_CONSTRUCTION_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRUCK
    ),
  },
  NUMBER_OF_AXLES: {
    title: i18n.t('entity.vehicle.labels.axleCount'),
    href: vehiclesRoutes.editV2,
    formField: 'axleCount',
    isAvailableFor: excludeVehicleTypes(
      VehicleTypeEnumObject.VEHICLETYPE_CARAVAN,
      VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,
      VehicleTypeEnumObject.VEHICLETYPE_VAN,
      VehicleTypeEnumObject.VEHICLETYPE_MOTORCYCLE,
      VehicleTypeEnumObject.VEHICLETYPE_TRUCK,
      VehicleTypeEnumObject.VEHICLETYPE_BUS,
      VehicleTypeEnumObject.VEHICLETYPE_OTHER,
      VehicleTypeEnumObject.VEHICLETYPE_AGRICULTURAL_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_CONSTRUCTION_MACHINE,
      VehicleTypeEnumObject.VEHICLETYPE_SEMI_TRUCK
    ),
  },

  // Vehicle edit form - vehicle data tab
  CONDITION: {
    title: i18n.t('entity.vehicle.labels.condition'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'condition',
  },
  COUNTRY_OF_ORIGIN: {
    title: i18n.t('entity.vehicle.labels.countryOfOrigin'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'originCountry',
  },
  COUNTRY_OF_FIRST_REGISTRATION: {
    title: i18n.t('entity.vehicle.labels.firstRegistrationCountry'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'firstRegistrationCountry',
  },
  COUNTRY_OF_LAST_REGISTRATION: {
    title: i18n.t('entity.vehicle.labels.lastRegistrationCountry'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'lastRegistrationCountry',
  },
  MILEAGE: {
    title: i18n.t('entity.vehicle.labels.mileage'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'mileage',
    isAvailableFor: notTrailerOrSemitrailer,
  },
  PREVIOUS_OWNERS: {
    title: i18n.t('entity.vehicle.labels.previousOwners'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'ownerCount',
  },
  NUMBER_OF_KEYS: {
    title: i18n.t('entity.vehicle.labels.numberOfKeysFull'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'primaryKeyCount',
  },
  VEHICLE_REGISTRATION_NUMBER: {
    title: i18n.t('entity.vehicle.labels.vehicleRegistration'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'vehicleRegistrationNumber',
  },
  REGISTRATION_CERTIFICATION_NUMBER: {
    title: i18n.t('entity.vehicle.labels.registrationCertificate'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'registrationCertificateNumber',
  },
  SOURCE_OF_VEHICLE: {
    title: i18n.t('entity.vehicle.labels.vehicleSource'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'vehicleSourceCodeId',
  },
  SALES_PROGRAM: {
    title: i18n.t('entity.vehicle.labels.salePreset'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'salePresetId',
  },
  SERVICE_BOOK_TYPE: {
    title: i18n.t('entity.vehicle.labels.serviceBook'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'serviceBookType',
  },
  SERVICE_BOOK_STATE: {
    title: i18n.t('entity.vehicle.labels.serviceBookState'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'serviceBookState',
  },
  LAST_SERVICE_DATE: {
    title: i18n.t('entity.vehicle.labels.dateOfLastInspection'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'lastServiceDate',
  },
  LAST_SERVICE_MILEAGE: {
    title: i18n.t('entity.vehicle.labels.lastService'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'lastServiceMileage',
  },
  TECHNICAL_INSPECTION_VALID_UNTIL: {
    title: i18n.t('entity.vehicle.labels.inspectionValidUntil'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'technicalInspectionValidUntil',
  },
};
